import { useState, useEffect } from 'react';
import { tryonAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, RotateCcw, Download, Share2, Heart, Loader2, Sparkles } from 'lucide-react';

const GARMENT_TYPES = [
  { value: 'suit', label: 'Suit' },
  { value: 'jacket', label: 'Jacket' },
  { value: 'shirt', label: 'Shirt' },
  { value: 'sherwani', label: 'Sherwani' },
  { value: 'bandhgala', label: 'Bandhgala' },
  { value: 'nehru_vest', label: 'Nehru Vest' },
  { value: 'kurta', label: 'Kurta' },
];

export default function StepTryOn({ wizardState, updateState, nextStep, prevStep, goToStep }) {
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [error, setError] = useState(null);

  const garmentType = wizardState.selectedGarmentType || 'suit';
  const fabric = wizardState.selectedFabric;
  const clientPhoto = wizardState.clientPhoto;

  // Auto-generate on mount if we have both fabric and photo
  useEffect(() => {
    if (fabric && clientPhoto && !wizardState.tryOnImageUrl && !generating) {
      generateTryOn();
    }
  }, []); // Only on mount

  const generateTryOn = async () => {
    if (!fabric) { toast.error('Please select a fabric first'); return; }
    if (!clientPhoto) { toast.error('A photo is needed for try-on'); return; }

    setGenerating(true);
    setError(null);
    try {
      const res = await tryonAPI.generate({
        fabric_id: fabric.id,
        client_id: wizardState.client?.id,
        client_photo_url: clientPhoto,
        garment_type: garmentType,
        mode: 'quick_try',
      });

      const imageUrl = res.data?.image_url || res.data?.generated_image_url;
      if (imageUrl) {
        updateState({ tryOnImageUrl: imageUrl, tryOnResult: res.data });
        setGenerated(true);
      } else {
        setError('Generation completed but no image was returned. Please try again.');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Try-on generation failed. Please try again.';
      setError(msg);
      toast.error(msg);
    }
    setGenerating(false);
  };

  const changeGarment = (newType) => {
    updateState({ selectedGarmentType: newType, tryOnImageUrl: null, tryOnResult: null });
    setGenerated(false);
  };

  const sharePreview = async () => {
    if (!wizardState.tryOnImageUrl) return;
    try {
      const response = await fetch(wizardState.tryOnImageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${wizardState.client?.first_name || 'look'}-tryon.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Image downloaded for sharing');
    } catch {
      // Fallback: open in new tab
      window.open(wizardState.tryOnImageUrl, '_blank');
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="step-tryon">
      <div className="text-center mb-8">
        <h1 className="text-3xl lg:text-4xl font-light tracking-tight" style={{ color: 'var(--brand-navy)', fontFamily: "'Playfair Display', serif" }}>
          See Your Look
        </h1>
        <p className="mt-2 text-base" style={{ color: 'var(--ink-muted)' }}>
          AI-powered preview of your bespoke garment
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
        {/* Image area */}
        <div className="relative">
          {generating ? (
            <div className="aspect-[3/4] rounded-2xl flex flex-col items-center justify-center"
              style={{ backgroundColor: 'rgba(27,42,74,0.03)', border: '1px solid rgba(27,42,74,0.06)' }}>
              <div className="relative">
                <Loader2 className="w-12 h-12 animate-spin" style={{ color: 'var(--brand-gold)' }} />
                <Sparkles className="w-5 h-5 absolute -top-1 -right-1 animate-pulse" style={{ color: 'var(--brand-gold)' }} />
              </div>
              <p className="mt-6 text-lg font-light" style={{ color: 'var(--brand-navy)', fontFamily: "'Playfair Display', serif" }}>
                Creating your look...
              </p>
              <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>This usually takes 10-20 seconds</p>
            </div>
          ) : wizardState.tryOnImageUrl ? (
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src={wizardState.tryOnImageUrl}
                alt="Try-on preview"
                className="w-full rounded-2xl"
                data-testid="tryon-preview-image"
              />
            </div>
          ) : error ? (
            <div className="aspect-[3/4] rounded-2xl flex flex-col items-center justify-center p-8 text-center"
              style={{ backgroundColor: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.1)' }}>
              <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
              <Button onClick={generateTryOn} className="mt-4 rounded-full gap-2" style={{ backgroundColor: 'var(--brand-navy)' }}>
                <RotateCcw className="w-4 h-4" /> Try Again
              </Button>
            </div>
          ) : (
            <div className="aspect-[3/4] rounded-2xl flex flex-col items-center justify-center"
              style={{ backgroundColor: 'rgba(27,42,74,0.03)', border: '1px dashed rgba(27,42,74,0.15)' }}>
              {!clientPhoto ? (
                <>
                  <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No photo available for try-on</p>
                  <Button onClick={() => goToStep(1)} variant="outline" className="mt-3 rounded-full text-xs gap-1">
                    <ArrowLeft className="w-3 h-3" /> Go Back to Add Photo
                  </Button>
                </>
              ) : (
                <Button onClick={generateTryOn} className="rounded-full h-12 px-8 gap-2 shadow-lg" style={{ backgroundColor: 'var(--brand-gold)', color: 'var(--brand-navy)' }}
                  data-testid="generate-tryon-btn">
                  <Sparkles className="w-5 h-5" /> Generate Preview
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Right panel: controls */}
        <div className="space-y-8">
          {/* Fabric info */}
          <div className="flex items-start gap-4 p-5 rounded-2xl" style={{ backgroundColor: 'white', border: '1px solid rgba(27,42,74,0.06)' }}>
            {fabric?.swatch_image_url || fabric?.swatch_url ? (
              <img src={fabric.swatch_image_url || fabric.swatch_url} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-xl flex-shrink-0" style={{ backgroundColor: 'rgba(27,42,74,0.04)' }} />
            )}
            <div>
              <p className="text-base font-semibold" style={{ color: 'var(--brand-navy)' }}>{fabric?.mill_name}</p>
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                {[fabric?.colour, fabric?.pattern].filter(Boolean).join(' · ')}
              </p>
              <p className="text-lg font-bold mt-1 tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                This look: &#8377;{wizardState.inclusivePrice?.toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          {/* Garment type selector */}
          <div>
            <p className="text-xs uppercase tracking-widest font-medium mb-3" style={{ color: 'var(--ink-muted)' }}>Garment Style</p>
            <div className="flex flex-wrap gap-2">
              {GARMENT_TYPES.map(g => (
                <button
                  key={g.value}
                  onClick={() => changeGarment(g.value)}
                  className="px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200"
                  style={{
                    backgroundColor: garmentType === g.value ? 'var(--brand-navy)' : 'white',
                    color: garmentType === g.value ? 'white' : 'var(--brand-navy)',
                    border: garmentType === g.value ? '2px solid var(--brand-navy)' : '1px solid rgba(27,42,74,0.12)',
                  }}
                  data-testid={`garment-type-${g.value}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          {generated && wizardState.tryOnImageUrl && (
            <div className="space-y-3">
              <Button onClick={nextStep}
                className="w-full h-14 rounded-full text-base font-semibold gap-2 shadow-lg"
                style={{ backgroundColor: 'var(--brand-gold)', color: 'var(--brand-navy)' }}
                data-testid="proceed-to-order-btn">
                <Heart className="w-5 h-5" /> Love It &mdash; Proceed to Order
              </Button>

              <div className="grid grid-cols-2 gap-3">
                <Button onClick={() => goToStep(0)} variant="outline" className="h-11 rounded-full text-xs gap-1.5">
                  <RotateCcw className="w-3.5 h-3.5" /> Try Another Fabric
                </Button>
                <Button onClick={sharePreview} variant="outline" className="h-11 rounded-full text-xs gap-1.5"
                  data-testid="share-preview-btn">
                  <Download className="w-3.5 h-3.5" /> Share Preview
                </Button>
              </div>

              <Button onClick={generateTryOn} variant="ghost" className="w-full h-10 rounded-full text-xs gap-1.5" style={{ color: 'var(--ink-muted)' }}>
                <RotateCcw className="w-3 h-3" /> Regenerate This Look
              </Button>
            </div>
          )}

          {/* No photo warning */}
          {!clientPhoto && (
            <div className="p-4 rounded-xl text-center" style={{ backgroundColor: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
              <p className="text-sm" style={{ color: '#D97706' }}>A photo is needed for the AI try-on preview</p>
              <Button onClick={() => goToStep(1)} variant="outline" className="mt-3 rounded-full text-xs">
                Go Back & Add Photo
              </Button>
            </div>
          )}

          {/* Back */}
          <Button variant="ghost" onClick={prevStep} className="gap-2 text-xs" style={{ color: 'var(--ink-muted)' }}>
            <ArrowLeft className="w-3 h-3" /> Back
          </Button>
        </div>
      </div>
    </div>
  );
}
